import { OneTrustModal } from '@maverick/cms';
import {
    Calendar,
    Category,
    HomeLogo,
    Menu,
    NavigationProps,
    NavigationVariant,
    OptionGroup,
    Product,
    Restaurant,
    SocialMediaNavigation,
} from '@maverick/entity';
import { CmsFooter, FooterMenuItemProps, OutbackHeader, SkipLink } from '@maverick/ui';
import { DictionaryPhrases } from '@sitecore-jss/sitecore-jss-nextjs';
import axios from 'axios';
import { Config } from 'src/Config';
import { MenuLink } from 'components/_shared/MenuLink';
import { Location } from '../shared-components/cms/Location';
import Link from 'next/link';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthenticationHandler } from '@maverick/authentication';
import { HeaderInteraction } from '../shared-components/cms//HeaderInteraction';
import { useEffect, useState } from 'react';
import { GetHomeLogo, GetSocialMedias } from '@maverick/sitecore';
import { SocialMediaIcons } from 'components/_shared/SocialMediaIcons/SocialMediaIcons';

const cmsUrl = Config.Env.IsLocal ? 'http://localhost:3000' : Config.Urls.Cms;
const brand = Config.Brand;
const env = Config.Env.Is;
export const REVALIDATE_DELAY = 1;

const api = axios.create({
    baseURL: cmsUrl,
    headers: {
        host: cmsUrl.replace(/^[^:]+:\/\//, ''),
        accept: '*/*',
        'user-agent': `Maverick-${brand}-CMS/v4 (${env}; ${cmsUrl})`,
    },
});

export interface BrowsableMenuPageProps {
    params: {
        restaurantSlug?: string;
        categoryId?: string;
        productId?: string;
        restaurantMenu?: Menu | null;
        restaurantInfo?: string | null;
        productModifiers?: Array<OptionGroup> | null;
        selectedProduct?: Product | null;
        selectedCategory?: Category | null;
        ghostProductId?: number | null;
        items: NavigationProps;
        dictionary: DictionaryPhrases | null;
        auth0Config: typeof Config.Auth0;
    };
}

export const getEmptyNavigationItems = () => {
    const navigationItems: NavigationProps = {
        mainItems: [],
        hamburgerItems: [],
        featuredItems: [],
        unfeaturedItems: [],
    };
    return navigationItems;
};

export const retrieveRestaurantBySlug = async (params: any) => {
    try {
        if (params.restaurantSlug === `restaurant`) {
            console.warn(`[retrieveRestaurantBySlug] params.restaurantSlug is 'restaurant'`);
            return null;
        }

        const { status, data } = await api.get(`api/olo/v1.1/restaurants/byslug/${params.restaurantSlug}`, {
            headers: { 'x-mav-cache': 3_600 },
        });
        if (status !== 200 || !data) {
            console.warn(`[retrieveRestaurantBySlug] Status: ${status}`);
            return null;
        }

        return Restaurant.Make(data);
    } catch (err) {
        console.error('Error retrieving restaurant by slug', err);
        return null;
    }
};

export const retrieveRestaurantMenu = async (restaurant: Restaurant | null) => {
    if (!restaurant) {
        console.warn(`[retrieveRestaurantMenu] Restaurant is null`);
        return null;
    }

    try {
        const { status, data } = await api.get(`api/olo/v1.1/restaurants/${restaurant.id}/menu`, {
            headers: { 'x-mav-cache': 3_600 },
        });
        if (status !== 200 || !data) {
            console.warn(`[retrieveRestaurantMenu] Status: ${status}`);
            return null;
        }

        return data as Menu;
    } catch (err) {
        console.error(`[retrieveRestaurantMenu] Error: ${err}`);
        return null;
    }
};

export const retrieveRestaurantWithCalendar = async (restaurant: Restaurant | null) => {
    if (!restaurant) {
        console.warn(`[retrieveRestaurantWithCalendar] Restaurant is null`);
        return null;
    }

    try {
        const now = new Date();
        const today = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(
            now.getDate()
        ).padStart(2, '0')}`;

        const { status, data } = await api.get(
            `api/olo/v1.1/restaurants/${restaurant.id}/calendars?from=${today}&to=${today}`
        );
        if (status !== 200 || !data) {
            console.warn(`[retrieveRestaurantWithCalendar] Status: ${status}`);
            return null;
        }

        const businessCalendar = data.calendar.find((c: any) => c.type === 'business');
        const curbsideCalendar = data.calendar.find((c: any) => c.type === 'curbsidepickup');
        const deliveryCalendar = data.calendar.find((c: any) => c.type === 'delivery');

        const result = {
            businessCalendar: businessCalendar ? Calendar.Make(businessCalendar) : null,
            curbsideCalendar: curbsideCalendar ? Calendar.Make(curbsideCalendar) : null,
            deliveryCalendar: deliveryCalendar ? Calendar.Make(deliveryCalendar) : null,
        };

        const calendar = () => {
            const calendarArray = [result.businessCalendar, result.curbsideCalendar, result.deliveryCalendar];
            const resultArray = calendarArray.filter((c) => !!c);
            return resultArray.length > 0 ? resultArray : undefined;
        };

        const newRestaurant = { ...restaurant, calendars: calendar() };
        return newRestaurant as Restaurant;
    } catch (error) {
        console.error(`[retrieveRestaurantWithCalendar] Error: ${error}`);
        return null;
    }
};

export const retrieveProductModifiers = async (params: any) => {
    if (!params.productId) {
        console.warn(`[retrieveProductModifiers] params.productId is null`);
        return null;
    }

    try {
        const { status, data } = await api.get(`api/olo/v1.1/products/${params.productId}/modifiers`, {
            headers: { 'x-mav-cache': 3_600 },
        });
        if (status !== 200 || !data) {
            console.warn(`[retrieveProductModifiers] Status: ${status}`);
            return null;
        }

        return data.optiongroups as Array<OptionGroup>;
    } catch (err) {
        console.error(`[retrieveProductModifiers] Error: ${err}`);
        return null;
    }
};

export const GetHeader = (items: NavigationProps, auth0Config: typeof Config.Auth0) => {
    const [logoItems, setLogoItems] = useState<HomeLogo | null>(null);

    useEffect(() => {
        GetHomeLogo(Config.Brand).then((response) => {
            if (response?.body) {
                setLogoItems(response.body as HomeLogo);
            }
        });
    }, []);

    return (
        <>
            <SkipLink text={'SKIP TO MAIN CONTENT'} link={'main-content'} />
            <SkipLink text={'SKIP TO FOOTER'} link={'footer'} />
            <OutbackHeader
                linkComponent={Link}
                items={items}
                img={
                    <img
                        src={logoItems?.item.Logo.src}
                        alt={logoItems?.item.Logo.alt}
                        width={logoItems?.item.Logo.width}
                        height={logoItems?.item.Logo.height}
                    />
                }
                imgMobile={
                    <img
                        src={logoItems?.item.LogoMobile.src}
                        alt={logoItems?.item.LogoMobile.alt}
                        width={logoItems?.item.LogoMobile.width}
                        height={logoItems?.item.LogoMobile.height}
                    />
                }
                extra={
                    <Auth0Provider
                        domain={auth0Config.domain}
                        clientId={auth0Config.clientId}
                        redirectUri={auth0Config.redirectUriCms}
                        audience={auth0Config.audience}
                        useRefreshTokens
                        cacheLocation='localstorage'
                        brand={Config.Brand}
                    >
                        <AuthenticationHandler />
                        <Location />
                        <HeaderInteraction />
                    </Auth0Provider>
                }
            />
        </>
    );
};

export const GetFooter = (items: NavigationProps) => {
    const [socialMediaItems, setSocialMediaItems] = useState<SocialMediaNavigation | null>(null);

    useEffect(() => {
        GetSocialMedias(Config.Brand).then((response) => {
            if (response?.body) {
                setSocialMediaItems(response.body as SocialMediaNavigation);
            }
        });
    }, []);

    const menuItems: FooterMenuItemProps[] | undefined = items.featuredItems?.map((item) => {
        return {
            variant: NavigationVariant.Featured,
            link: MenuLink(item.url, item.label, !item.newTab),
        };
    });
    items.unfeaturedItems?.forEach((item) => {
        menuItems?.push({
            variant: NavigationVariant.Unfeatured,
            link: MenuLink(item.url, item.label, !item.newTab),
        });
    });

    return (
        <CmsFooter
            footerMenuItems={menuItems}
            socialMediaIcons={SocialMediaIcons(
                socialMediaItems?.item?.Instagram?.value ?? '',
                socialMediaItems?.item?.Facebook?.value ?? '',
                socialMediaItems?.item?.Twitter?.value ?? ''
            )}
            oneTrustModal={<OneTrustModal />}
        />
    );
};

export const buildMenuMetaDescription = (restaurantInfo: Restaurant | null, tagline: string): string => {
    const locationInfo = restaurantInfo ? ` at ${restaurantInfo.name}` : '';
    return `View the full Outback Steakhouse menu${locationInfo}. ${tagline}. See prices, nutrition info, and order online.`;
};

export const buildCategoryMetaDescription = (
    restaurantInfo: Restaurant | null,
    category: Category | null,
    tagline: string,
    categoryId: number
): string => {
    if (categoryId === 0 || !category) {
        return buildMenuMetaDescription(restaurantInfo, tagline);
    }
    const locationInfo = restaurantInfo ? ` at ${restaurantInfo.name}` : ' at Outback Steakhouse';
    const productNames = (category.products ?? []).slice(0, 3).map((p) => p.name);
    const productsSegment = productNames.length ? ` ${productNames.join(', ')} & more.` : '';
    return `${category.name}${locationInfo}.${productsSegment} Order online.`;
};
